import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useChatAnalytics } from 'app/analytics';
import { off, on } from 'app/socket';
import { Nullable } from 'shared_DEPRECATED/types';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';
import { dateUtils } from 'shared_DEPRECATED/utils';

import {
  CHAT_FORM_FIELD_NAMES,
  CHAT_MESSAGE_TYPES,
} from 'features/chat/config';
import { ChatFormValues } from 'features/chat/config/types/ChatForm';
import {
  Message,
  MessageAttachmentProcessed,
} from 'features/chat/config/types/Message';
import {
  ScheduledMessage,
  ScheduledMessageEdit,
  ScheduledMessageSubmit,
} from 'features/chat/config/types/ScheduledMessage';
import { useChatMessageScheduleCreate } from 'features/chat/hooks/mutation/useChatMessageScheduleCreate';
import { useChatScheduledMessagesQuery } from 'features/chat/hooks/query/useChatScheduledMessages';
import { updateMessage } from 'features/chat/utils/messageUpdaters';

import { getUuId } from 'shared/utils/uuid';

import { EditMessageNullable } from '../config/types/ParentMessage';
import { useChatMessageScheduleEdit } from '../hooks/mutation/useChatMessageScheduleEdit';
import { useChatInterlocutorContext } from './chatInterlocutor';

type ChatMessageSchedulingContextType = {
  isScheduledListVisible: boolean;
  isCreateScheduleMessageButtonVisible: boolean;
  isViewScheduledMessagesButtonVisible: boolean;
  hasScheduledMessages: boolean;
  showScheduledMessages: () => void;
  hideScheduledMessages: () => void;
  scheduledMessages: ScheduledMessage[];
  handleScheduledMessageSent: (message: Message) => void;
  updateScheduledMessageProcessingStatus: (
    message: MessageAttachmentProcessed
  ) => void;
  createScheduledMessage: ScheduledMessageSubmit;
  editScheduledMessage: ScheduledMessageEdit;
  createScheduledAttachmentMessage: ScheduledMessageSubmit;
};

const ChatMessageSchedulingContext = React.createContext<
  ChatMessageSchedulingContextType | undefined
>(undefined);

type MessageSchedulingProviderProps = {
  children: React.ReactNode;
};

export const ChatMessageSchedulingProvider = ({
  children,
}: MessageSchedulingProviderProps) => {
  const [isScheduledListVisible, setIsScheduledListVisible] = useState(false);

  const editScheduledMessageMutation = useChatMessageScheduleEdit();

  const { chatId } = useChatInterlocutorContext();

  const { setNewScheduledMessages, scheduledMessages } =
    useChatScheduledMessagesQuery({ chatId });

  const { setValue, control } = useFormContext<ChatFormValues>();

  const { sendChatMessageScheduledAnalyticsEvent } = useChatAnalytics();

  const messageText = useWatch({
    control: control,
    name: CHAT_FORM_FIELD_NAMES.TEXT,
  });

  const onScheduledMessageCreateSuccess = useCallback(
    (message: Nullable<ScheduledMessage>) => {
      sendChatMessageScheduledAnalyticsEvent();

      setValue(CHAT_FORM_FIELD_NAMES.TEXT, '');
    },
    [sendChatMessageScheduledAnalyticsEvent, setValue]
  );

  const showScheduledMessages = useCallback(() => {
    if (!isScheduledListVisible) {
      setIsScheduledListVisible(true);
    }
  }, [setIsScheduledListVisible, isScheduledListVisible]);

  const hideScheduledMessages = useCallback(() => {
    if (isScheduledListVisible) {
      setIsScheduledListVisible(false);
    }
  }, [setIsScheduledListVisible, isScheduledListVisible]);

  const mutation = useChatMessageScheduleCreate({
    onSuccess: onScheduledMessageCreateSuccess,
  });

  const hasScheduledMessages = scheduledMessages.length > 0;

  const isCreateScheduleMessageButtonVisible =
    !isScheduledListVisible && !!messageText;

  const isViewScheduledMessagesButtonVisible =
    hasScheduledMessages && !isScheduledListVisible && !messageText;

  const createScheduledMessage = useCallback<ScheduledMessageSubmit>(
    async ({ text, scheduledFor, linkPreview, parentMessage }) => {
      await mutation.mutateAsync({
        text,
        scheduledFor,
        replyTo: parentMessage?.messageId ?? null,
        attachments: [],
        linkPreview: linkPreview || null,
      });
      showScheduledMessages();
    },
    [mutation, showScheduledMessages]
  );

  const createScheduledAttachmentMessage = useCallback<ScheduledMessageSubmit>(
    async ({
      text,
      scheduledFor,
      attachments = [],
      linkPreview,
      parentMessage,
    }) => {
      const tempMessageId = getUuId();
      const newMessage: ScheduledMessage = {
        chatId,
        messageId: tempMessageId,
        temporaryMessageId: tempMessageId,
        fromUserId: '',
        text,
        scheduledFor,
        attachments,
        replyTo: null,
        reactions: {},
        createdAt: dateUtils(dateUtils.utc()).formatAsISOString(),
        deleted: false,
        type: CHAT_MESSAGE_TYPES.PLAIN,
        content: {
          type: CHAT_MESSAGE_TYPES.PLAIN,
          text,
          attachments,
          linkPreview: linkPreview || null,
        },
      };
      showScheduledMessages();
      setNewScheduledMessages((prev) => [newMessage, ...prev]);

      const onAttachmentsUploaded = async (attachments: Attachment[]) => {
        try {
          await mutation.mutateAsync({
            text,
            scheduledFor,
            replyTo: parentMessage?.messageId ?? null,
            attachments,
            linkPreview: linkPreview || null,
          });
        } finally {
          off('chat_message_attachments_uploaded', onAttachmentsUploaded);
        }
      };

      on('chat_message_attachments_uploaded', onAttachmentsUploaded);
    },
    [chatId, mutation, showScheduledMessages, setNewScheduledMessages]
  );

  const editScheduledMessage = useCallback<ScheduledMessageEdit>(
    async (messageText, editedMessage: EditMessageNullable, linkPreview) => {
      if (!editedMessage) {
        return;
      }

      await editScheduledMessageMutation.mutateAsync({
        text: messageText,
        messageId: editedMessage.messageId,
        replyTo: editedMessage.replyTo!,
        attachments: editedMessage.attachments as Attachment[],
        scheduledFor: editedMessage.scheduledFor,
        linkPreview: linkPreview ?? null,
      });
    },
    [editScheduledMessageMutation]
  );

  const handleScheduledMessageSent = useCallback(
    (message: { scheduledMessageId?: string; messageId: string }) => {
      const { scheduledMessageId } = message;
      setNewScheduledMessages((prev) =>
        prev.filter((msg) => msg.messageId !== scheduledMessageId)
      );
    },
    [setNewScheduledMessages]
  );

  const updateScheduledMessageProcessingStatus = useCallback(
    (message: MessageAttachmentProcessed) => {
      setNewScheduledMessages((prev) =>
        updateMessage(prev, (msg) => {
          if (msg.messageId !== message.messageId) {
            return null;
          }

          const oldAttachments = msg?.attachments ?? [];

          const newAttachments = oldAttachments.map((attachment) => {
            const attachmentId =
              'attachmentId' in attachment ? attachment.attachmentId : '';

            return attachmentId === message.attachmentId
              ? {
                  ...attachment,
                  processingStatus: message.processingStatus,
                  url: `${attachment.url}?ts=${Date.now()}`,
                }
              : attachment;
          });

          return {
            ...msg,
            attachments: newAttachments,
          } as ScheduledMessage;
        })
      );
    },
    [setNewScheduledMessages]
  );

  const value = useMemo(
    () => ({
      isScheduledListVisible,
      isCreateScheduleMessageButtonVisible,
      isViewScheduledMessagesButtonVisible,
      hasScheduledMessages,
      showScheduledMessages,
      hideScheduledMessages,
      createScheduledMessage,
      editScheduledMessage,
      scheduledMessages: scheduledMessages ?? [],
      handleScheduledMessageSent,
      createScheduledAttachmentMessage,
      updateScheduledMessageProcessingStatus,
    }),
    [
      isScheduledListVisible,
      isCreateScheduleMessageButtonVisible,
      isViewScheduledMessagesButtonVisible,
      hasScheduledMessages,
      showScheduledMessages,
      hideScheduledMessages,
      createScheduledMessage,
      editScheduledMessage,
      scheduledMessages,
      handleScheduledMessageSent,
      createScheduledAttachmentMessage,
      updateScheduledMessageProcessingStatus,
    ]
  );

  return (
    <ChatMessageSchedulingContext.Provider value={value}>
      {children}
    </ChatMessageSchedulingContext.Provider>
  );
};

export const useChatMessageSchedulingContext = () => {
  const context = useContext(ChatMessageSchedulingContext);

  if (context === undefined) {
    throw new Error(
      'useChatMessageSchedulingContext is use outside of the ChatMessageSchedulingContext'
    );
  }

  return context;
};
